import { ArrowRight, MapPin, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { PublicPropertySummary } from '@/lib/api';
import { propertyStageLabel } from '@/lib/labels';
import { priceLabel, propertyTypeLabel, specsLine } from '@/lib/property-display';

export function PropertyCard({ property }: { property: PublicPropertySummary }) {
  const cover = property.photos[0];
  return (
    <Link
      to={`/empreendimento/${property.slug}`}
      className="group block overflow-hidden rounded-2xl border bg-card transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-primary/5"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        {cover ? (
          <img
            src={cover}
            alt={property.name}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
            Sem foto
          </div>
        )}
        {property.stage && (
          <div className="absolute left-3 top-3 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-sm">
            {propertyStageLabel[property.stage]}
          </div>
        )}
        {property.featured && (
          <div className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-background/95 px-3 py-1 text-xs font-semibold text-primary shadow-sm">
            <Star className="h-3.5 w-3.5 fill-current" />
            Destaque
          </div>
        )}
        <div className="absolute bottom-3 left-3 rounded-full bg-background/95 px-3 py-1.5 text-xs font-semibold text-primary shadow-sm backdrop-blur">
          {priceLabel(property, 'Consulte preço')}
        </div>
      </div>

      <div className="p-5">
        <h3 className="text-lg font-bold leading-tight text-foreground group-hover:text-primary">
          {property.name}
        </h3>
        {property.tagline && (
          <p className="mt-1.5 text-sm leading-snug text-foreground/80">{property.tagline}</p>
        )}
        <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
          <MapPin className="h-4 w-4 shrink-0" />
          <span>
            {property.neighborhood ? `${property.neighborhood}, ` : ''}
            {property.city} · {property.state}
          </span>
        </p>

        {property.kind === 'listing' ? (
          <p className="mt-3 text-sm font-medium text-foreground">
            {[
              property.propertyType && propertyTypeLabel[property.propertyType],
              specsLine(property.specs),
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        ) : property.unitTypes.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {property.unitTypes.slice(0, 3).map((u, i) => (
              <span
                key={i}
                className="rounded-md bg-secondary px-2 py-0.5 text-xs text-secondary-foreground"
              >
                {u.label}
              </span>
            ))}
          </div>
        )}

        <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary opacity-70 transition-opacity group-hover:opacity-100">
          Ver detalhes
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </div>
      </div>
    </Link>
  );
}
